import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Card, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Screen } from '../../components';
import { getClientHost } from '../../lib/constants/constants';
import {
  getContentfulArticleCards,
  getContentfulRecipeCards,
  getContentfulVideoCards,
} from '../../lib/contentful/getContentfulContent';
import { OrdersContext } from '../../lib/contexts/OrdersContext';
import { ThemeContext } from '../../lib/contexts/ThemeContext';
import { UserContext } from '../../lib/contexts/UserContext';
import { toCapitalizeFromUnderScore } from '../../lib/helpers/helpers';
import { ContentCard } from '../LearningCenterScreen/components/ContentCard';
import DietQualityGoalCard from './components/DietQualityGoalCard';
import MiniDeliveryDetails from './components/MiniDeliveryDetails';
import OrderDetails from './components/OrderDetails';
import ProgramInfoCard from './components/ProgramInfoCard';
import NewProgramInfoCard from './components/NewProgramInfoCard';
import CircularProgress from '@mui/material/CircularProgress';

const CMSContent = () => {
  const [articles, setArticles] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    // Flag to prevent state updates after component unmounts
    let isMounted = true;

    //getContentfulGuidebookCards().then(setGuidebooks).catch(console.error);
    getContentfulArticleCards()
      .then((e) => e.slice(0, 3))
      .then((data) => {
        if (isMounted) setArticles(data);
      })
      .catch(console.error);

    getContentfulRecipeCards()
      .then((e) => e.slice(0, 3))
      .then((data) => {
        if (isMounted) setRecipes(data);
      })
      .catch(console.error);

    getContentfulVideoCards()
      .then((e) => e.slice(0, 3))
      .then((data) => {
        if (isMounted) setVideos(data);
      })
      .catch(console.error);

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);

  const renderData = useMemo(() => {
    return {
      articles,
      recipes,
      videos,
    };
  }, [articles, recipes, videos]);

  return (
    <>
      {Object.entries(renderData).map(([category, data]) => (
        <React.Fragment key={category}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SectionTitle sx={{ paddingBottom: 0 }}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </SectionTitle>
            <Button
              size="small"
              component={RouterLink}
              to={`/learn/${category.toLowerCase()}`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{
                    color: 'var(--primary-main, #211551)',
                    fontFamily: 'Montserrat',
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '22px',
                    letterSpacing: '0.46px',
                    textTransform: 'uppercase',
                  }}
                >
                  VIEW ALL
                </Typography>
                <ArrowForwardIcon sx={{ width: 18, height: 18, flexShrink: 0, marginLeft: '4px' }} />
              </Box>
            </Button>
          </Box>
          <Divider sx={{ borderColor: '#E0E0E0', mb: 2 }} />
          <Grid
            container
            spacing={2}
            sx={{ marginTop: '10px', marginBottom: '25px', alignItems: 'center', justifyContent: 'center' }}
          >
            {data.map((item) => (
              <ContentCard
                key={item.id}
                title={item.title}
                imageUrl={item.imageUrl}
                linkPath={`/learn/${category.toLowerCase()}/${item.id}`}
              />
            ))}
          </Grid>
        </React.Fragment>
      ))}
    </>
  );
};

const SectionTitle = ({ sx, ...props }) => {
  return (
    <Typography
      variant="h6"
      sx={{
        // color: order.status === 'Future' ? 'white' : 'var(--Gray-gray12, #171717)',
        leadingTrim: 'both',
        textEdge: 'cap',
        paddingBottom: '18px',
        fontFamily: 'Montserrat',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        ...sx,
      }}
      {...props}
    />
  );
};

const OrderManagementScreen = () => {
  const {
    state: { ordersData, isLoading, dataFetched },
  } = useContext(OrdersContext);
  const {
    state: { firstName },
  } = useContext(UserContext);
  const [thisWeekOrders, setThisWeekOrders] = useState([]);
  const {
    state: { darkMode },
  } = useContext(ThemeContext);
  const [programInfo, setProgramInfo] = useState({
    name: 'No Program',
    weeksLeft: 0,
    isActive: false,
    mealsPerWeek: 0,
    totalWeeks: 0,
    startDate: '',
    endDate: '',
    reEnrollTime: '',
  });
  // console.log('this is the API_HOST: ', API_HOST);
  // const [dietQuality, setDietQuality] = useState({
  //   currentScore: 6,
  //   currentDate: '2024-12-09',
  //   goalScore: 9
  // });
  const navigate = useNavigate();
  const isMobileView = useMediaQuery('(max-width:600px)');

  const isSmallScreen = useMediaQuery('(max-width:1250px)');

  useEffect(() => {
    if (ordersData) {
      setThisWeekOrders(ordersData.thisWeekOrders || []);
      setProgramInfo(ordersData.programInfo || {});
    }
  }, [ordersData]);

  // useEffect(() => {
  //   console.log(' this is programInfo in OrderManagementScreen: ', programInfo);
  // }, [programInfo]);

  const availableOrders = Array.isArray(ordersData) && ordersData.length >= 3 ? ordersData.slice(0, 3) : [];

  const getOrdersList = () => {
    if (isMobileView) {
      return (
        <Box
          id="mobile-orders-list"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: dataFetched ? 'flex-start' : 'center',
            gap: '13px',
            alignSelf: 'stretch',
            borderRadius: '8px',
            overflowX: 'hidden',
            width: '100%',
          }}
        >
          <Box
            sx={{ display: 'flex', flexDirection: 'row', cursor: 'pointer' }}
            onClick={handleNavigateToAllOrders}
          >
            <SectionTitle>All Orders</SectionTitle>
            <ArrowForwardIcon
              sx={{ color: '#6B7280', cursor: 'pointer', ml: 1 }}
              onClick={handleNavigateToAllOrders}
            />
          </Box>
          {isLoading ? (
            <CircularProgress />
          ) : dataFetched && availableOrders.length > 0 ? (
            <Card
              id="mini-orders-container"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--3-5, 16px)',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                width: '100%',
                paddingBottom: '10px',
              }}
            >
              {availableOrders.map((order) => (
                <MiniDeliveryDetails
                  key={order.id}
                  order={order}
                  smallerWidth={true}
                />
              ))}
            </Card>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray11, #6F6F6F)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                No orders yet
              </Typography>
              <InfoIcon
                sx={{
                  width: 'var(--1-5, 24px)',
                  height: '24px',
                }}
              />
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Grid
        id="all-orders-container"
        item
        xs={12}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'row', cursor: 'pointer' }}
          onClick={handleNavigateToAllOrders}
        >
          <Typography
            variant="h6"
            sx={{
              // color: order.status === 'Future' ? 'white' : 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              paddingBottom: '18px',
              fontFamily: 'Montserrat',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            All Orders
          </Typography>
          <ArrowForwardIcon
            sx={{ color: '#6B7280', cursor: 'pointer', ml: 1 }}
            onClick={handleNavigateToAllOrders}
          />
        </Box>
        <Divider sx={{ borderColor: '#E0E0E0', mb: 2 }} />

        <Box
          sx={{
            // borderBottom: '1px solid #E0E0E0',
            pb: 2,
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : dataFetched && availableOrders.length > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {availableOrders.map((order) => (
                <Box key={order.id}>
                  <OrderDetails
                    order={order}
                    showPopUps={false}
                    from="/orders"
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                height: '100%',
                // paddingBottom: '400px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray11, #6F6F6F)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                No orders yet
              </Typography>
              <InfoIcon
                sx={{
                  width: 'var(--1-5, 24px)',
                  height: '24px',
                }}
              />
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const handleNavigateToAllOrders = () => {
    navigate('/orders/details');
  };

  const handleTakeAssessment = () => {
    const assessmentUrl = `${getClientHost()}/assessment/start`;
    // console.log('this is getClientHost: ', getClientHost());
    window.open(assessmentUrl, '_blank');
  };

  const handleNavigateToOrderDetails = (orderId) => {
    navigate(`/orders/${orderId}`, { state: { from: '/orders' } });
  };

  return (
    <Screen showSidebarNav={true}>
      <Box
        sx={{ padding: isMobileView ? '16px' : '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Grid
          container
          spacing={3}
          sx={{ width: '100%', maxWidth: isMobileView ? '100%' : '1200px', justifyContent: 'center' }}
        >
          {/* Header */}
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 'bold', fontFamily: 'Montserrat' }}
            >
              Hi, {firstName ? toCapitalizeFromUnderScore(firstName) : 'there'}!
            </Typography>
            {!isMobileView && (
              <Button
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  // backgroundColor: darkMode ? '#444' : 'white',
                  // color: darkMode ? '#fff' : '#1A237E',
                  // backgroundColor: darkMode ? '#303F9F' : '#303F9F',
                  color: 'white',
                  border: '1px solid var(--color12, #1C124D)',
                  background: 'var(--color12, #1C124D)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  '&:hover': {
                    backgroundColor: darkMode ? '#303F9F' : '#303F9F',
                    color: 'white',
                    border: '3px solid #1A237E',
                    borderRadius: '8px',
                  },
                }}
                endIcon={<ArrowForwardIcon />}
                onClick={handleTakeAssessment}
              >
                <Typography
                  sx={{
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Re-Assess Your Diet
                </Typography>
              </Button>
            )}
          </Grid>

          {/* Top Row */}
          <Grid
            item
            id="top-row-container"
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: isSmallScreen ? 'column' : 'row',
              gap: 4,
              justifyContent: 'center',
              width: '100%',
              marginBottom: isMobileView ? 6 : 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: isMobileView ? 'center' : 'flex-start',
                width: '100%',
                marginBottom: isMobileView ? 2 : 0, // Add spacing between cards for mobile view
              }}
            >
              <DietQualityGoalCard />
            </Box>
            <Box
              sx={{
                flex: 2,
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end', // Right-align the NewProgramInfoCard
                marginBottom: isMobileView ? 2 : 0, // Add spacing between cards for mobile view
              }}
            >
              {/* <ProgramInfoCard /> */}
              <NewProgramInfoCard />
            </Box>
          </Grid>

          {/* Bottom Row: All Orders */}
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            {getOrdersList()}
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'center', width: '100%', flexDirection: 'column' }}
          >
            <CMSContent />
          </Grid>
        </Grid>
      </Box>
    </Screen>
  );
};

export default OrderManagementScreen;
